import React, { useState, useEffect, useRef } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { V1 } from '@frontend/api-frontend-gateway';
import { hasSchemaACL, getUser } from '@frontend/auth-helpers';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Heading,
  Text,
  ContentLoader,
  TextLink,
  Table,
  Button,
  Chip,
  Modal,
  useModalControl,
  TextField,
  useAlert,
  useForm,
  ConfirmationModal,
  BannerNotification,
} from '@frontend/design-system';

type SiteInfo = NonNullable<V1.Types.GetSiteConfigIO['output']['siteConfig']>;

const useSiteData = (site: string) => {
  const result = V1.Queries.useGetSiteConfigQuery({
    request: {
      siteName: site,
    },
    options: {
      retry: 2,
    },
  });

  return result;
};

const useSiteReleases = (site: string) => {
  const result = V1.Queries.useGetPublishedSiteConfigsQuery({
    request: {
      siteName: site,
    },
    options: {
      retry: 2,
    },
  });

  return result;
};

/* Since we don't have a direct way to see the production site name from the edge site, we derive it here */
/* IMPORTANT IF YOU ADD NEW SITE WITH EDGE YOU NEED TO ADD IT TO THIS LIST, UPDATE THIS FUNCTION */
const getProductionSiteName = (site: string): string | null => {
  if (site === 'edge.getweave.com') {
    return 'app.getweave.com';
  } else if (site === 'deployment-playground-edge.weavelab.ninja') {
    return 'deployment-playground.weavelab.ninja';
  } else {
    return null;
  }
};

const useMarkBuildsUnpromotable = () => {
  return V1.Queries.useFetchMarkSiteBuildsAsUnpromotable();
};

const useMarkBuildRangeUnpromotable = () => {
  return V1.Queries.useFetchMarkSiteBuildRangeAsUnpromotable();
};

const useUpdateSiteConfig = () => {
  return V1.Queries.useFetchPublishSiteConfig();
};

const useSiteLock = () => {
  return V1.Queries.useFetchLockSiteConfig();
};
const useSiteUnlock = () => {
  return V1.Queries.useFetchUnlockSiteConfig();
};

type MetadataEntry = {
  key: string;
  value: string;
  id: string; // Add unique ID for form fields
};

const UpdateMetadataModal = ({
  show,
  onClose,
  currentMetadata,
  onSave,
  isLoading = false,
  configVersion,
}: {
  show: boolean;
  onClose: () => void;
  currentMetadata?: Record<string, any>;
  onSave: (metadata: Record<string, any>) => Promise<void>;
  isLoading?: boolean;
  configVersion?: string;
}) => {
  const [metadataEntries, setMetadataEntries] = useState<MetadataEntry[]>(() => {
    if (currentMetadata) {
      return Object.entries(currentMetadata).map(([key, value], index) => ({
        key,
        value: String(value),
        id: `entry-${index}`,
      }));
    }
    return [{ key: '', value: '', id: 'entry-0' }];
  });

  // Update metadata entries when modal opens with new current metadata
  useEffect(() => {
    if (show && currentMetadata) {
      setMetadataEntries(
        Object.entries(currentMetadata).map(([key, value], index) => ({
          key,
          value: String(value),
          id: `entry-${index}`,
        }))
      );
    } else if (show && !currentMetadata) {
      // If no current metadata, start with empty entry
      setMetadataEntries([{ key: '', value: '', id: 'entry-0' }]);
    }
  }, [show, currentMetadata]);

  const addEntry = () => {
    const newId = `entry-${Date.now()}`;
    setMetadataEntries([...metadataEntries, { key: '', value: '', id: newId }]);
  };

  const removeEntry = (index: number) => {
    setMetadataEntries(metadataEntries.filter((_, i) => i !== index));
  };

  const updateEntry = (index: number, field: 'key' | 'value', newValue: string) => {
    const updated = [...metadataEntries];
    updated[index][field] = newValue;
    setMetadataEntries(updated);
  };

  const handleSave = async () => {
    const metadata = metadataEntries
      .filter((entry) => entry.key.trim() !== '')
      .reduce((acc, entry) => {
        acc[entry.key] = entry.value;
        return acc;
      }, {} as Record<string, string>);

    //TODO: uncomment once ACLS are in place
    await onSave(metadata);
    onClose();
  };

  const handleCancel = () => {
    // Reset to original metadata
    if (currentMetadata) {
      setMetadataEntries(
        Object.entries(currentMetadata).map(([key, value], index) => ({
          key,
          value: String(value),
          id: `entry-${index}`,
        }))
      );
    } else {
      setMetadataEntries([{ key: '', value: '', id: 'entry-0' }]);
    }
    onClose();
  };

  return (
    <Modal show={show} onClose={handleCancel}>
      <Modal.Header>
        <Heading level={2}>Update Metadata</Heading>
        {configVersion && (
          <Text color='subdued' css={{ marginTop: theme.spacing(1) }}>
            Config version: {configVersion.substring(0, 8)}
          </Text>
        )}
      </Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <div>
            <Text color='subdued'>Add or modify metadata key-value pairs for this site configuration.</Text>
            {currentMetadata && Object.keys(currentMetadata).length > 0 && (
              <Text color='subdued' css={{ marginTop: theme.spacing(1), fontSize: theme.fontSize(12) }}>
                Currently has {Object.keys(currentMetadata).length} metadata entr
                {Object.keys(currentMetadata).length === 1 ? 'y' : 'ies'}
              </Text>
            )}
          </div>

          {metadataEntries.map((entry, index) => (
            <div key={entry.id} css={{ display: 'flex', gap: theme.spacing(2), alignItems: 'end' }}>
              <div css={{ flex: 1 }}>
                <TextField
                  name={`key-${entry.id}`}
                  label='Key'
                  placeholder='Enter metadata key'
                  disabled={isLoading}
                  value={entry.key}
                  onChange={(event) => {
                    const target = (event as React.ChangeEvent<HTMLInputElement>)?.target;
                    if (target) {
                      updateEntry(index, 'key', target.value || '');
                    }
                  }}
                  id={`key-${entry.id}`}
                  onBlur={() => {}}
                  onFocus={() => {}}
                  active={false}
                  error=''
                  aria-invalid={false}
                  touched={false}
                />
              </div>
              <div css={{ flex: 1 }}>
                <TextField
                  name={`value-${entry.id}`}
                  label='Value'
                  placeholder='Enter metadata value'
                  disabled={isLoading}
                  value={entry.value}
                  onChange={(event) => {
                    const target = (event as React.ChangeEvent<HTMLInputElement>)?.target;
                    if (target) {
                      updateEntry(index, 'value', target.value || '');
                    }
                  }}
                  id={`value-${entry.id}`}
                  onBlur={() => {}}
                  onFocus={() => {}}
                  active={false}
                  error=''
                  aria-invalid={false}
                  touched={false}
                />
              </div>
              <Button
                variant='tertiary'
                size='small'
                onClick={() => removeEntry(index)}
                disabled={metadataEntries.length === 1 || isLoading}
                css={{ marginBottom: theme.spacing(1) }}
              >
                <Icon name='delete' />
              </Button>
            </div>
          ))}

          <Button variant='tertiary' size='small' onClick={addEntry}>
            <Icon name='add' /> Add Entry
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: isLoading ? 'Saving...' : 'Save Metadata',
          onClick: handleSave,
          disabled: isLoading,
        }}
        secondary={{
          label: 'Cancel',
          onClick: handleCancel,
          disabled: isLoading,
        }}
      />
    </Modal>
  );
};

type ConfirmableAction = 'mark-unpromotable' | 'mark-high-urgency';

const getConfirmationModalConfig = (
  action: ConfirmableAction | null
): { title: string; message: string; destructive: boolean } => {
  switch (action) {
    case 'mark-unpromotable':
      return {
        title: 'Confirm Mark Unpromotable',
        message: 'Are you sure you want to mark this build as unpromotable?',
        destructive: false,
      };
    case 'mark-high-urgency':
      return {
        title: 'Confirm High Urgency',
        message: 'Are you sure you want to mark this release as high urgency?',
        destructive: false,
      };
    default:
      return { title: 'Confirm Action', message: '', destructive: false };
  }
};

const LockSiteModal = ({
  show,
  onClose,
  onSave,
  isLoading = false,
  siteName,
}: {
  show: boolean;
  onClose: () => void;
  onSave: (reason: string) => Promise<void>;
  isLoading?: boolean;
  siteName?: string;
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (show) {
      setReason('');
    }
  }, [show]);

  const handleSave = async () => {
    await onSave(reason);
    onClose();
  };

  const handleCancel = () => {
    setReason('');
    onClose();
  };

  return (
    <Modal show={show} onClose={handleCancel}>
      <Modal.Header>
        <Heading level={2}>Lock Site</Heading>
        {siteName && (
          <Text color='subdued' css={{ marginTop: theme.spacing(1) }}>
            Site: {siteName}
          </Text>
        )}
      </Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <Text color='subdued'>Please provide a reason for locking this site.</Text>
          <TextField
            name='lock-reason'
            label='Reason'
            placeholder='Enter reason for locking'
            disabled={isLoading}
            value={reason}
            onChange={(event) => {
              const target = (event as React.ChangeEvent<HTMLInputElement>)?.target;
              if (target) {
                setReason(target.value || '');
              }
            }}
            id='lock-reason'
            onBlur={() => {}}
            onFocus={() => {}}
            active={false}
            error=''
            aria-invalid={false}
            touched={false}
          />
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: isLoading ? 'Locking...' : 'Lock Site',
          onClick: handleSave,
          disabled: isLoading,
        }}
        secondary={{
          label: 'Cancel',
          onClick: handleCancel,
          disabled: isLoading,
        }}
      />
    </Modal>
  );
};

const RollbackReleaseModal = ({
  show,
  onClose,
  onSave,
  isLoading = false,
  siteName,
  rollbackToVersion,
  currentVersion,
}: {
  show: boolean;
  onClose: () => void;
  onSave: (reasonForRollback: string) => Promise<void>;
  isLoading?: boolean;
  siteName?: string;
  rollbackToVersion?: string;
  currentVersion?: string;
}) => {
  const prevShowRef = useRef(false);

  const { getFieldProps, values, isComplete, reset } = useForm({
    fields: {
      reasonForRollback: { type: 'text', required: true },
    },
  });

  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (show && !prevShowRef.current) {
      reset();
    }
    prevShowRef.current = show;
  }, [show, reset]);

  const handleSave = async () => {
    if (!isComplete) return;
    await onSave(values.reasonForRollback as string);
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Modal show={show} onClose={handleCancel}>
      <Modal.Header>
        <Heading level={2}>Roll Back To Release</Heading>
        {siteName && rollbackToVersion && (
          <Text color='subdued' css={{ marginTop: theme.spacing(1) }}>
            Rolling back {siteName} to version {rollbackToVersion.substring(0, 8)}
          </Text>
        )}
      </Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <Text color='subdued'>Please provide a reason for rolling back to this release.</Text>
          {currentVersion && rollbackToVersion && (
            <BannerNotification
              status='warn'
              message={`All builds between ${currentVersion.substring(0, 8)} and ${rollbackToVersion.substring(
                0,
                8
              )} will be marked as unpromotable. The site will also be locked after rollback.`}
            />
          )}
          <TextField
            {...getFieldProps('reasonForRollback')}
            label='Reason for Rollback'
            placeholder='Enter reason for rolling back'
            disabled={isLoading}
          />
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: isLoading ? 'Rolling Back...' : 'Roll Back',
          onClick: handleSave,
          disabled: isLoading || !isComplete,
        }}
        secondary={{
          label: 'Cancel',
          onClick: handleCancel,
          disabled: isLoading,
        }}
      />
    </Modal>
  );
};

const PromoteReleaseModal = ({
  show,
  onClose,
  onSave,
  isLoading = false,
  siteName,
  promoteToSite,
}: {
  show: boolean;
  onClose: () => void;
  onSave: (promoter: string, reasonForPromoting: string, promotingFor?: string) => Promise<void>;
  isLoading?: boolean;
  siteName?: string;
  promoteToSite?: string;
}) => {
  const user = getUser();
  const defaultPromoter = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '';
  const prevShowRef = useRef(false);

  const { getFieldProps, values, isComplete, reset } = useForm({
    fields: {
      promoter: { type: 'text', required: true, value: defaultPromoter },
      reasonForPromoting: { type: 'text', required: true },
      promotingFor: { type: 'text' },
    },
  });

  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (show && !prevShowRef.current) {
      reset();
    }
    prevShowRef.current = show;
  }, [show, reset]);

  const handleSave = async () => {
    if (!isComplete) return;
    await onSave(
      values.promoter as string,
      values.reasonForPromoting as string,
      (values.promotingFor as string)?.trim() || undefined
    );
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Modal show={show} onClose={handleCancel}>
      <Modal.Header>
        <Heading level={2}>Promote Release</Heading>
        {siteName && promoteToSite && (
          <Text color='subdued' css={{ marginTop: theme.spacing(1) }}>
            Promoting from {siteName} to {promoteToSite}
          </Text>
        )}
      </Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <Text color='subdued'>Please provide the promoter and reason for promoting this release.</Text>
          <TextField
            {...getFieldProps('promoter')}
            label='Promoter'
            placeholder='Enter your name or identifier'
            disabled
          />
          <TextField
            {...getFieldProps('reasonForPromoting')}
            label='Reason for Promoting'
            placeholder='Enter reason for promoting'
            disabled={isLoading}
          />
          <TextField
            {...getFieldProps('promotingFor')}
            label='Promoting on behalf of (Optional)'
            placeholder='Enter who this promotion is for'
            disabled={isLoading}
          />
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: isLoading ? 'Promoting...' : 'Promote Release',
          onClick: handleSave,
          disabled: isLoading || !isComplete,
        }}
        secondary={{
          label: 'Cancel',
          onClick: handleCancel,
          disabled: isLoading,
        }}
      />
    </Modal>
  );
};

export const SiteDetails = ({ site, acls = [] }: { site: string; acls?: Permission[] }) => {
  const canEdit = acls.length > 0 ? hasSchemaACL('weave', acls) : false;
  const { data: siteConfigResponse, isLoading, error, refetch: refetchSiteConfig } = useSiteData(site);
  const { data: siteReleasesResponse, isLoading: releasesLoading, refetch: refetchReleases } = useSiteReleases(site);
  const updateSiteConfig = useUpdateSiteConfig();
  const updateBuildPromotable = useMarkBuildsUnpromotable();
  const markBuildRangeUnpromotable = useMarkBuildRangeUnpromotable();
  const lockSiteConfig = useSiteLock();
  const unlockSiteConfig = useSiteUnlock();
  const alerts = useAlert();
  const metadataModal = useModalControl();
  const lockModal = useModalControl();
  const promoteModal = useModalControl();
  const rollbackModal = useModalControl();
  const confirmActionModal = useModalControl();
  const [selectedConfig, setSelectedConfig] = useState<SiteInfo | null>(null);
  const [promoteConfig, setPromoteConfig] = useState<{ config: SiteInfo; promoteToSite: string } | null>(null);
  const [rollbackConfig, setRollbackConfig] = useState<{ config: SiteInfo; rollbackToSite: string } | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: ConfirmableAction;
    config: SiteInfo;
  } | null>(null);

  /* Opens confirmation modal for marking a build as unpromotable */
  const handleRequestMarkUnpromotable = (config: SiteInfo) => {
    setPendingAction({ type: 'mark-unpromotable', config });
    confirmActionModal.openModal();
  };

  /* This function handles marking a build as unpromotable */
  const handleMarkingUnPromotable = async (config: SiteInfo) => {
    setIsUpdating(true);
    try {
      await updateBuildPromotable({
        request: {
          siteName: site,
          bucketPrefix: config.bucketPrefix,
          bucketName: config.bucketName,
        },
      });

      await Promise.all([refetchSiteConfig(), refetchReleases()]);
      alerts.success('Promotable status updated successfully.');
    } catch (error) {
      alerts.error('Failed to update promotable status. Please try again.');
      console.error('Failed to update promotable status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenPromoteModal = (config: SiteInfo, promoteToSite: string) => {
    if (!config || !promoteToSite) return;
    setPromoteConfig({ config, promoteToSite });
    promoteModal.openModal();
  };

  const handlePromoteBuild = async (promoter: string, reasonForPromoting: string, promotingFor?: string) => {
    if (!promoteConfig) return;
    const { config, promoteToSite } = promoteConfig;
    setIsUpdating(true);
    try {
      await updateSiteConfig({
        request: {
          ...config,
          metadata: {
            ...config.metadata,
            promoter,
            reasonForPromoting,
            ...(promotingFor && { promotingFor }),
          },
          siteName: promoteToSite,
        },
      });

      await Promise.all([refetchSiteConfig(), refetchReleases()]);
      alerts.success('Promotion request submitted successfully. Go to the production site to see the new release.');
    } catch (error) {
      alerts.error('Failed to promote release. Please try again.');
      console.error('Failed to promote release:', error);
    } finally {
      setIsUpdating(false);
      setPromoteConfig(null);
    }
  };

  /* Opens confirmation modal for setting urgency to high */
  const handleRequestHighUrgency = (config: SiteInfo) => {
    setPendingAction({ type: 'mark-high-urgency', config });
    confirmActionModal.openModal();
  };

  /* used for setting low urgency to High urgency */
  const handleUpdateUrgency = async (config: SiteInfo) => {
    if (!config) return;

    setIsUpdating(true);

    try {
      await updateSiteConfig({
        request: {
          ...config,
          publicMetadata: {
            ...config.publicMetadata,
            urgency: 'high',
          },
          siteName: config.siteName,
        },
      });

      await Promise.all([refetchSiteConfig(), refetchReleases()]);
      alerts.success('Urgency update request submitted successfully.');
    } catch (error) {
      alerts.error('Failed to update urgency. Please try again.');
      console.error('Failed to update urgency:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  /* Opens rollback modal */
  const handleRequestRollback = (config: SiteInfo, rollbackToSite: string) => {
    setRollbackConfig({ config, rollbackToSite });
    rollbackModal.openModal();
  };

  /* Used to rollback to the previous release */
  const handleRollbackToBuild = async (reasonForRollback: string) => {
    if (!rollbackConfig || !siteConfigResponse?.siteConfig) return;
    const { config, rollbackToSite } = rollbackConfig;
    const currentConfig = siteConfigResponse.siteConfig;

    setIsUpdating(true);

    try {
      // Mark all builds between rollback target and current as unpromotable
      await markBuildRangeUnpromotable({
        request: {
          siteName: rollbackToSite,
          fromBucketName: config.bucketName,
          fromBucketPrefix: config.bucketPrefix,
          toBucketName: currentConfig.bucketName,
          toBucketPrefix: currentConfig.bucketPrefix,
        },
      });
      alerts.info('Builds between rollback target and current have been marked as unpromotable.');

      await updateSiteConfig({
        request: {
          ...config,
          metadata: {
            ...config.metadata,
            reasonForRollback,
          },
          publicMetadata: {
            ...config.publicMetadata,
            urgency: 'high', // Set urgency to high on rollback
          },
          siteName: rollbackToSite,
        },
      });

      // Lock the site after rollback using the same reason
      await lockSiteConfig({
        request: {
          reason: reasonForRollback,
          siteName: rollbackToSite,
        },
      });
      alerts.info('Site has been locked after rollback to ensure stability.');

      await Promise.all([refetchSiteConfig(), refetchReleases()]);
      alerts.success('Rollback request submitted successfully. Go to the production site to see the new release.');
    } catch (error) {
      alerts.error('Failed to rollback release. Please try again.');
      console.error('Failed to rollback release:', error);
    } finally {
      setIsUpdating(false);
      setRollbackConfig(null);
    }
  };

  const handleLockSite = (config: SiteInfo) => {
    setSelectedConfig(config);
    lockModal.openModal();
  };

  const handleLockSiteWithReason = async (reason: string) => {
    if (!selectedConfig) return;

    setIsUpdating(true);
    try {
      await lockSiteConfig({
        request: {
          reason,
          siteName: selectedConfig.siteName,
        },
      });

      await Promise.all([refetchSiteConfig(), refetchReleases()]);
      alerts.success('Lock request submitted successfully.');
    } catch (error) {
      alerts.error('Failed to lock site. Please try again.');
      console.error('Failed to lock site:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUnlockSite = async (config: SiteInfo) => {
    if (!config) return;

    setIsUpdating(true);

    try {
      await unlockSiteConfig({
        request: {
          siteName: config.siteName,
        },
      });

      await Promise.all([refetchSiteConfig(), refetchReleases()]);
      alerts.success('Unlock request submitted successfully.');
    } catch (error) {
      alerts.error('Failed to unlock site. Please try again.');
      console.error('Failed to unlock site:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateMetadata = (config: SiteInfo) => {
    setSelectedConfig(config);
    metadataModal.openModal();
  };

  const handleSaveMetadata = async (metadata: Record<string, any>) => {
    if (!selectedConfig) return;

    setIsUpdating(true);
    try {
      // Create the update payload with all existing config data plus new metadata
      const updatePayload = {
        ...selectedConfig,
        metadata: metadata, // Use the new metadata
      };

      await updateSiteConfig({
        request: {
          siteName: site,
          ...updatePayload,
        },
      });

      await Promise.all([refetchSiteConfig(), refetchReleases()]);
      alerts.success('Metadata updated successfully.');
    } catch (error) {
      console.error('Failed to update metadata:', error);
      alerts.error('Failed to update metadata. Please try again.');
    } finally {
      setIsUpdating(false);
      setSelectedConfig(null);
    }
  };

  /* Handles confirming the pending action from the modal */
  const handleConfirmAction = () => {
    if (!pendingAction) return;

    switch (pendingAction.type) {
      case 'mark-unpromotable':
        handleMarkingUnPromotable(pendingAction.config);
        break;
      case 'mark-high-urgency':
        handleUpdateUrgency(pendingAction.config);
        break;
    }
    setPendingAction(null);
  };

  const handleCloseConfirmModal = () => {
    setPendingAction(null);
    confirmActionModal.closeModal();
  };

  if (isLoading) {
    return (
      <div css={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(12) }}>
        <ContentLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div css={{ padding: theme.spacing(6), textAlign: 'center' }}>
        <Text color='error'>Failed to load site configuration</Text>
      </div>
    );
  }

  if (!siteConfigResponse?.siteConfig) {
    return (
      <div css={{ padding: theme.spacing(6), textAlign: 'center' }}>
        <Text color='subdued'>No site configuration found</Text>
      </div>
    );
  }

  return (
    <div css={{ padding: theme.spacing(6) }}>
      <div css={{ maxWidth: '800px' }}>
        <SiteConfigInfo
          siteConfig={siteConfigResponse.siteConfig}
          onUpdateMetadata={handleUpdateMetadata}
          handleUnlockSite={handleUnlockSite}
          handleLockSite={handleLockSite}
          canEdit={canEdit}
        />
      </div>
      {releasesLoading ? (
        <div css={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(6) }}>
          <ContentLoader />
        </div>
      ) : (
        <SiteReleasesTable
          siteReleases={siteReleasesResponse?.publishedSiteConfigs}
          currentConfig={siteConfigResponse.siteConfig}
          onPromoteBuild={handleOpenPromoteModal}
          onUpdatePromotable={handleRequestMarkUnpromotable}
          handleUpdateUrgency={handleRequestHighUrgency}
          onRollbackToBuild={handleRequestRollback}
          onUpdateMetadata={handleUpdateMetadata}
          canEdit={canEdit}
        />
      )}

      <UpdateMetadataModal
        {...metadataModal.modalProps}
        currentMetadata={selectedConfig?.metadata}
        onSave={handleSaveMetadata}
        isLoading={isUpdating}
        configVersion={selectedConfig?.version}
      />

      <LockSiteModal
        {...lockModal.modalProps}
        onSave={handleLockSiteWithReason}
        isLoading={isUpdating}
        siteName={selectedConfig?.siteName}
      />

      <PromoteReleaseModal
        {...promoteModal.modalProps}
        onSave={handlePromoteBuild}
        isLoading={isUpdating}
        siteName={promoteConfig?.config.siteName}
        promoteToSite={promoteConfig?.promoteToSite}
      />

      <RollbackReleaseModal
        {...rollbackModal.modalProps}
        onSave={handleRollbackToBuild}
        isLoading={isUpdating}
        siteName={rollbackConfig?.config.siteName}
        rollbackToVersion={rollbackConfig?.config.version}
        currentVersion={siteConfigResponse.siteConfig.version}
      />

      <ConfirmationModal
        {...confirmActionModal.modalProps}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmAction}
        title={getConfirmationModalConfig(pendingAction?.type || null).title}
        destructive={getConfirmationModalConfig(pendingAction?.type || null).destructive}
      >
        <Text>{getConfirmationModalConfig(pendingAction?.type || null).message}</Text>
      </ConfirmationModal>
    </div>
  );
};

const SiteReleasesTable = ({
  siteReleases,
  currentConfig,
  canEdit,
  onUpdateMetadata,
  onPromoteBuild,
  onRollbackToBuild,
  onUpdatePromotable,
  handleUpdateUrgency,
}: {
  siteReleases: V1.Types.GetPublishedSiteConfigsIO['output']['publishedSiteConfigs'];
  currentConfig: SiteInfo;
  onUpdateMetadata: (config: SiteInfo) => void;
  onPromoteBuild: (config: SiteInfo, promoteToSite: string) => void;
  handleUpdateUrgency: (config: SiteInfo) => void;
  onRollbackToBuild: (config: SiteInfo, rollbackToSite: string) => void;
  onUpdatePromotable: (config: SiteInfo) => void;
  canEdit: boolean;
}) => {
  if (!siteReleases || siteReleases.length === 0) {
    return (
      <ConfigSection title='Previous Releases'>
        <div css={{ padding: theme.spacing(4), textAlign: 'center' }}>
          <Text color='subdued'>No previous releases found</Text>
        </div>
      </ConfigSection>
    );
  }
  return (
    <ConfigSection title='Previous Releases'>
      <div
        css={{
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.medium,
          width: '100%',
          overflow: 'hidden',
          overflowX: 'auto',
        }}
      >
        <Table
          data={siteReleases}
          styleConfig={{
            rows: (rowData) =>
              rowData.version === currentConfig.version
                ? {
                    backgroundColor: theme.colors.success5,
                    '&:hover': {
                      backgroundColor: theme.colors.success10,
                    },
                  }
                : undefined,
          }}
          colConfig={[
            {
              Header: '#',
              id: 'index',
              accessor: (_, index) => index + 1,
              maxWidth: 60,
            },
            {
              Header: 'Version',
              id: 'version',
              accessor: (release) => release.version,
              cellRenderer: (_, release) => {
                const metadata = release?.metadata;
                let summaryText = 'Summary unavailable';
                if (metadata) {
                  const commitMessage = metadata.commit_message?.replace(/"/g, '') || 'No commit message';
                  const author = metadata.commit_author?.replace(/"/g, '') || 'Unknown author';
                  const prNumber = metadata.pr_number;

                  if (prNumber && prNumber !== '' && prNumber !== 'No PR') {
                    summaryText = `${commitMessage} by ${author} • PR #${prNumber}`;
                  } else {
                    summaryText = `${commitMessage} by ${author}`;
                  }
                }

                return (
                  <div>
                    <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                      <TextLink
                        href={`https://github.com/weave-lab/frontend/commit/${release?.version}`}
                        target='_blank'
                        title={metadata?.source || ''}
                        rel='noopener noreferrer'
                      >
                        {release?.version?.substring(0, 8) || 'N/A'}
                      </TextLink>
                      {release?.version === currentConfig.version && (
                        <Chip variant='success' size='default'>
                          Current
                        </Chip>
                      )}
                    </div>
                    <div css={{ marginTop: theme.spacing(0.5) }}>
                      <Text
                        color='subdued'
                        css={{
                          fontSize: theme.fontSize(12),
                          fontStyle: 'italic',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: '1.4',
                        }}
                      >
                        {summaryText}
                      </Text>
                    </div>
                  </div>
                );
              },
              minWidth: 300,
            },
            {
              Header: 'Site Version',
              id: 'metadataVersion',
              accessor: (release) => {
                const version = release.metadata?.app_version?.replace(/"/g, '');
                return version && version !== 'undefined' ? version : 'N/A';
              },
              maxWidth: 120,
            },
            {
              Header: 'Build Timestamp',
              id: 'buildTimestamp',
              accessor: (release) => {
                const timestamp = release.metadata?.timestamp?.replace(/"/g, '');
                if (!timestamp) return 'N/A';

                // Parse timestamp format like "2025-09-24_16:10:36"
                try {
                  const formattedTimestamp = timestamp.replace(/_/, ' ');
                  const date = new Date(formattedTimestamp);
                  if (isNaN(date.getTime())) {
                    return timestamp; // Return raw if can't parse
                  }
                  return date.toLocaleString();
                } catch {
                  return timestamp; // Return raw if parsing fails
                }
              },
              minWidth: 150,
            },
            {
              Header: 'Published At',
              id: 'publishedAt',
              accessor: (release) => {
                if (!release.publishedAt) return 'N/A';
                return new Date(release.publishedAt).toLocaleString();
              },
            },
            {
              Header: 'Urgency',
              id: 'urgency',
              accessor: (release) => release.publicMetadata?.urgency || 'N/A',
              maxWidth: 80,
            },
            {
              Header: 'Promotable',
              id: 'promotable',
              accessor: (release) => {
                // Check if the release has skipPromotion flag, defaulting to promotable if not found
                const isPromotable = !release.skipPromotion;
                return (
                  <Chip variant={isPromotable ? 'success' : 'warn'} size='default'>
                    {isPromotable ? 'Promotable' : 'Unpromotable'}
                  </Chip>
                );
              },
            },
            {
              Header: 'Promoter',
              id: 'promoter',
              accessor: (release) => {
                const promoter = release.metadata?.promoter?.replace(/"/g, '') || 'N/A';
                const isManual = promoter === 'manual';

                if (promoter === 'N/A') {
                  return <Text color='subdued'>N/A</Text>;
                }

                return (
                  <Chip variant={isManual ? 'warn' : 'success'} size='default'>
                    {promoter}
                  </Chip>
                );
              },
            },
            {
              Header: 'Reason For Promotion',
              id: 'reasonForPromotion',
              accessor: (release) => {
                const reasonForPromoting = release.metadata?.reasonForPromoting?.replace(/"/g, '') || 'N/A';

                if (reasonForPromoting === 'N/A') {
                  return <Text color='subdued'>N/A</Text>;
                }

                return <Text>{reasonForPromoting}</Text>;
              },
            },
            {
              Header: 'Reason For Rollback',
              id: 'reasonForRollback',
              accessor: (release) => {
                const reasonForRollback = release.metadata?.reasonForRollback?.replace(/"/g, '') || 'N/A';

                if (reasonForRollback === 'N/A') {
                  return <Text color='subdued'>N/A</Text>;
                }

                return <Text>{reasonForRollback}</Text>;
              },
            },
            {
              Header: 'Bucket Prefix',
              id: 'bucketPrefix',
              accessor: (release) => release.bucketPrefix,
              cellRenderer: (_, release) => {
                return (
                  <TextLink
                    href={`https://console.cloud.google.com/storage/browser/${release?.bucketName}/${release?.bucketPrefix}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {release?.bucketPrefix || 'N/A'}
                  </TextLink>
                );
              },
            },
          ]}
          rowActions={{
            actions: [
              {
                Icon: () => <Icon name='update' />,
                label: 'Roll back to this release',
                hoverLabel: `bart frontend rollback `,
                onClick: (release) => {
                  onRollbackToBuild(release, release.siteName || '');
                },
                hide: (release) => release.version === currentConfig.version || !canEdit,
              },
              {
                Icon: () => <Icon name='upload' />,
                label: 'Promote Release',
                hoverLabel: 'bart frontend promote edge.getweave.com app.getweave.com',
                onClick: (release) => {
                  const productionSite = getProductionSiteName(release.siteName || '');
                  if (!productionSite) return;
                  onPromoteBuild(release, productionSite);
                },
                hide: (release) =>
                  (release.siteName && !release.siteName.includes('edge')) || release.skipPromotion || !canEdit,
              },
              {
                Icon: () => <Icon name='edit' />,
                label: 'Mark unpromotable',
                hoverLabel:
                  'Mark specific build as unpromotable bart frontend mark-unpromotable app.getweave.com --at mybucket:ab/cdef123 - Mark range of builds as unpromotable bart frontend mark-unpromotable app.getweave.com --from mybucket:bug-start --to mybucket:bug-fixed',
                onClick: (release) => {
                  onUpdatePromotable(release);
                },
                hide: (release) => release.version === currentConfig.version || !canEdit || release.skipPromotion,
              },
              {
                Icon: () => <Icon name='alert' />,
                label: 'Mark High Urgency',
                hoverLabel:
                  'bart frontend update-metadata app.getweave.com --metadata urgency=high --public-metadata feature=enabled',
                onClick: (release) => {
                  handleUpdateUrgency(release);
                },
                hide: (release) => (release.publicMetadata && release.publicMetadata.urgency !== 'low') || !canEdit,
              },
              {
                Icon: () => <Icon name='settings' />,
                label: 'Update metadata',
                hoverLabel: 'bart frontend update-metadata <site> [flags]',
                onClick: (release) => {
                  onUpdateMetadata(release);
                },
                hide: () => !canEdit,
              },
            ],
          }}
          isPaginated
          hasGlobalSearch
          clientPaginationConfig={{ defaultRowsPerPage: 30 }}
        />
      </div>
    </ConfigSection>
  );
};

const SiteConfigInfo = ({
  siteConfig,
  onUpdateMetadata,
  handleUnlockSite,
  handleLockSite,
  canEdit,
}: {
  siteConfig: SiteInfo;
  onUpdateMetadata: (config: SiteInfo) => void;
  handleUnlockSite: (config: SiteInfo) => void;
  handleLockSite: (config: SiteInfo) => void;
  canEdit: boolean;
}) => {
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  return (
    <div css={{ marginBottom: theme.spacing(6) }}>
      <div
        css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(4) }}
      >
        <div>
          <Heading level={3} css={{ color: theme.colors.neutral70 }}>
            Site Configuration
          </Heading>
          <TextLink href='https://github.com/weave-lab/frontend/blob/main/docs/serve-and-deploy.md'>
            Release Docs
          </TextLink>
        </div>

        {canEdit && (
          <div css={{ display: 'flex', gap: theme.spacing(1) }}>
            <Button
              variant='primary'
              size='small'
              hoverLabel='bart frontend lock app.getweave.com --reason "Emergency incident response"'
              onClick={() => {
                siteConfig.lock ? handleUnlockSite(siteConfig) : handleLockSite(siteConfig);
              }}
            >
              {siteConfig.lock ? 'Unlock Site' : 'Lock Site'}
            </Button>
            <Button
              hoverLabel='bart frontend update-metadata <site> [flags]'
              variant='primary'
              size='small'
              onClick={() => onUpdateMetadata(siteConfig)}
            >
              <Icon name='settings' /> Update Metadata
            </Button>
          </div>
        )}
      </div>

      {/* Primary Info Tiles */}
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: theme.spacing(2),
          marginBottom: theme.spacing(3),
        }}
      >
        <InfoTile
          title='Site Name'
          value={siteConfig.siteName}
          href={siteConfig.siteName ? `https://${siteConfig.siteName}` : undefined}
        />
        <InfoTile
          title='Version'
          value={siteConfig.version ? siteConfig.version.substring(0, 8) : undefined}
          href={siteConfig.version ? `https://github.com/weave-lab/frontend/commit/${siteConfig.version}` : undefined}
        />
        <InfoTile
          title='Published At'
          value={siteConfig.publishedAt ? new Date(siteConfig.publishedAt).toLocaleString() : undefined}
        />
        <InfoTile
          title='Bucket Prefix'
          value={siteConfig.bucketPrefix}
          href={
            siteConfig.bucketPrefix
              ? `https://console.cloud.google.com/storage/browser/${siteConfig.bucketName}/${siteConfig.bucketPrefix}`
              : undefined
          }
        />
        <LockTile lockInfo={siteConfig.lock} />
        <InfoTile title='Urgency' value={siteConfig.publicMetadata?.urgency} />
      </div>

      {/* More Info Section */}
      <div css={{ marginBottom: theme.spacing(4) }}>
        <Button
          variant='tertiary'
          size='small'
          onClick={() => setShowMoreInfo(!showMoreInfo)}
          css={{ marginBottom: theme.spacing(3) }}
        >
          {showMoreInfo ? 'Hide' : 'Show'} More Information
        </Button>

        {showMoreInfo && (
          <div css={{ display: 'grid', gap: theme.spacing(2) }}>
            {siteConfig.publicMetadata && <JsonField label='Public Metadata' value={siteConfig.publicMetadata} />}
            {siteConfig.metadata && <JsonField label='Internal Metadata' value={siteConfig.metadata} />}
            {siteConfig.canaryConfig && <JsonField label='Canary Config' value={siteConfig.canaryConfig} />}
            <JsonField label='Full Config' value={siteConfig} />
          </div>
        )}
      </div>
    </div>
  );
};

const InfoTile = ({ title, value, href }: { title: string; value: string | undefined; href?: string }) => {
  const displayValue = value || 'N/A';

  return (
    <div
      css={{
        padding: theme.spacing(2),
        backgroundColor: theme.colors.white,
        borderRadius: theme.borderRadius.small,
        border: `1px solid ${theme.colors.neutral20}`,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <div css={{ marginBottom: theme.spacing(1) }}>
        <Text weight='medium' color='subdued' css={{ fontSize: theme.fontSize(12) }}>
          {title}
        </Text>
      </div>
      <div>
        {href && value ? (
          <TextLink
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            css={{
              fontSize: theme.fontSize(14),
              fontWeight: 600,
              wordBreak: 'break-word',
            }}
          >
            {displayValue}
          </TextLink>
        ) : (
          <Text weight='semibold' css={{ fontSize: theme.fontSize(14), wordBreak: 'break-word' }}>
            {displayValue}
          </Text>
        )}
      </div>
    </div>
  );
};

const LockTile = ({ lockInfo }: { lockInfo: SiteInfo['lock'] }) => {
  const isLocked = !!lockInfo?.lockedAt;
  const reason = lockInfo?.reason;
  const lockedAt = lockInfo?.lockedAt;

  const getStatusText = () => {
    if (!isLocked) return 'No';
    if (lockedAt) {
      const formattedDate = new Date(lockedAt).toLocaleString();
      return `Yes - at ${formattedDate}`;
    }
    return 'Yes';
  };

  return (
    <div
      css={{
        padding: theme.spacing(2),
        backgroundColor: isLocked ? theme.colors.warning5 : theme.colors.white,
        borderRadius: theme.borderRadius.small,
        border: `1px solid ${isLocked ? theme.colors.warning20 : theme.colors.neutral20}`,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <div css={{ marginBottom: theme.spacing(1) }}>
        <Text weight='medium' color='subdued' css={{ fontSize: theme.fontSize(12) }}>
          Locked{' '}
          <Text weight='semibold' css={{ fontSize: theme.fontSize(14), wordBreak: 'break-word' }}>
            {getStatusText()}
          </Text>
        </Text>
      </div>
      <div>
        {isLocked && reason && (
          <div css={{ marginTop: theme.spacing(0.5) }}>
            <Text color='subdued' css={{ fontSize: theme.fontSize(12), wordBreak: 'break-word' }}>
              {reason}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

const ConfigSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div css={{ marginBottom: theme.spacing(6) }}>
      <Heading level={3} css={{ marginBottom: theme.spacing(3), color: theme.colors.neutral70 }}>
        {title}
      </Heading>
      <div css={{ display: 'grid', gap: theme.spacing(2) }}>{children}</div>
    </div>
  );
};

const JsonField = ({ label, value }: { label: string; value: object }) => {
  const jsonString = JSON.stringify(value, null, 2);

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(3),
        backgroundColor: theme.colors.white,
        borderRadius: theme.borderRadius.medium,
        border: `1px solid ${theme.colors.neutral20}`,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <div css={{ marginBottom: theme.spacing(2) }}>
        <Text weight='medium' color='default'>
          {label}:
        </Text>
      </div>
      <div>
        <Text
          color='subdued'
          css={{
            fontFamily: 'monospace',
            fontSize: theme.fontSize(12),
            whiteSpace: 'pre-wrap',
            backgroundColor: theme.colors.neutral5,
            padding: theme.spacing(2),
            borderRadius: theme.borderRadius.small,
            border: `1px solid ${theme.colors.neutral10}`,
          }}
        >
          {jsonString}
        </Text>
      </div>
    </div>
  );
};
